import { getPopupLayoutConfig } from "../popup-layouts/index.js";
import {
  parseBorderRadiusPx,
  resolveBorderRadiusCandidatePx,
} from "../../shared/brand-border-radius.js";
import { isExtensionPopup } from "../../shared/extension-preview.js";

export { parseBorderRadiusPx };

const DEFAULT_BORDER_RADIUS_PX = 20;

/**
 * Global popup shell corner radius (Brand & AI preset, layoutConfig, shell customCSS).
 *
 * @param {object | null | undefined} popupData
 * @param {{ activeStep?: number, isMobile?: boolean }} [options]
 * @returns {number} px value for inline styles / CSS variables
 */
export function resolvePopupThemeBorderRadius(popupData, options = {}) {
  if (isExtensionPopup(popupData)) return 4;

  const activeStep =
    typeof options.activeStep === "number" && !Number.isNaN(options.activeStep)
      ? options.activeStep
      : 0;
  const isMobile = Boolean(options.isMobile);

  const gs = popupData?.generalSettings || {};
  const layoutOptions =
    getPopupLayoutConfig(gs)?.options ||
    gs.layoutConfig?.options ||
    gs.layoutTree?.options ||
    {};

  const step = popupData?.schema?.[activeStep];
  const popupSettings = step?.popupSettings || {};
  const shellCss = popupSettings.customCSS || {};
  const shellSide = shellCss[isMobile ? "mobile" : "desktop"] || {};

  const candidates = [
    // Merchant Design → Style (shell customCSS) must win over layout defaults.
    shellSide.borderRadius,
    shellCss[isMobile ? "mobile" : "desktop"]?.borderRadius,
    shellCss.desktop?.borderRadius,
    shellCss.mobile?.borderRadius,
    shellCss.borderRadius,
    layoutOptions.borderRadius,
    gs.brandBorderRadius,
  ];

  for (const raw of candidates) {
    const px = resolveBorderRadiusCandidatePx(raw, "popup");
    if (px != null) return px;
  }

  return DEFAULT_BORDER_RADIUS_PX;
}

/**
 * @param {number} px
 * @returns {string}
 */
export function formatBorderRadiusCss(px) {
  const resolved = parseBorderRadiusPx(px) ?? DEFAULT_BORDER_RADIUS_PX;
  return `${resolved}px`;
}

/**
 * When border color was saved with alpha 0 (e.g. editor default `#0000`), use opaque RGB
 * so thickness + roundness still render a visible frame in preview.
 * @param {string | number | null | undefined} color
 * @returns {string | null}
 */
function resolveVisibleBorderColor(color) {
  if (color == null || color === "") return null;
  const s = String(color).trim();
  if (/^#[0-9a-fA-F]{8}$/.test(s)) {
    const alpha = parseInt(s.slice(7, 9), 16);
    if (alpha === 0) return s.slice(0, 7);
    if (alpha === 255) return s.slice(0, 7);
  }
  if (/^#[0-9a-fA-F]{4}$/.test(s)) {
    const alpha = parseInt(s[4] + s[4], 16);
    if (alpha === 0) {
      return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
    }
  }
  return s;
}

/**
 * Inline border + radius for cover-image frames (catalog slots, hero, strip items).
 * Bottom corners are squared only for Carousel cards (`cart-drawer-vertical-v1`);
 * all other offer types keep the full theme radius.
 *
 * @param {object | null | undefined} blockCSS
 * @param {number} borderRadiusPx
 * @param {{ squareBottomCorners?: boolean }} [options]
 * @returns {object}
 */
export function buildCoverImageFrameStyle(blockCSS, borderRadiusPx, options = {}) {
  const radius = formatBorderRadiusCss(borderRadiusPx);
  const squareBottom = options.squareBottomCorners === true;
  const style = squareBottom
    ? {
        borderTopLeftRadius: radius,
        borderTopRightRadius: radius,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxSizing: "border-box",
      }
    : {
        borderRadius: radius,
        boxSizing: "border-box",
      };

  const rawWidth = blockCSS?.borderWidth;
  const width =
    rawWidth != null && rawWidth !== ""
      ? typeof rawWidth === "number"
        ? `${rawWidth}px`
        : String(rawWidth)
      : null;
  const widthPx = width != null ? parseFloat(width) : 0;
  const color = resolveVisibleBorderColor(blockCSS?.borderColor);

  if (widthPx > 0 && color) {
    style.borderWidth = width;
    style.borderStyle = blockCSS?.borderStyle || "solid";
    style.borderColor = color;
  }

  return style;
}
